import { AuthGuard, redirectLoggedInTo } from '@angular/fire/auth-guard';
import { Routes } from '@angular/router';

import { PendingChangesGuard } from './core/guards/pending-changes.guard';
import { inactiveStatusGuard, lockedStatusGuard, statusGuard } from './core/guards/status.guard';

const redirectLoggedInToFolders = () => redirectLoggedInTo(['sections/folders']);

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./elements/authentication/login/login.component').then((m) => m.LoginComponent),
    canActivate: [AuthGuard],
    data: { authGuardPipe: redirectLoggedInToFolders },
  },

  {
    path: 'forgotpassword',
    loadComponent: () => import('./elements/authentication/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
  },

  {
    path: 'locked',
    loadComponent: () => import('./elements/authentication/status/locked/locked.component').then((m) => m.LockedComponent),
    canActivate: [lockedStatusGuard],
  },

  {
    path: 'resubscribe',
    loadComponent: () => import('./elements/authentication/status/resubscribe/resubscribe.component').then((m) => m.ResubscribeComponent),
    canActivate: [inactiveStatusGuard],
  },

  {
    path: 'sections',
    loadComponent: () => import('./screens/wrapper/wrapper.component').then((m) => m.WrapperComponent),
    canActivate: [statusGuard],
    children: [
      {
        path: 'folders',
        loadComponent: () => import('./screens/folders-projects/folders-projects.component').then((m) => m.FoldersProjectsComponent),
        canActivate: [statusGuard],
        children: [
          {
            path: ':folderId',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/projects/list/projects-list-wrapper/projects-list-wrapper.component').then((m) => m.ProjectsListWrapperComponent),
          },
        ],
      },
      {
        path: 'folders/:folderId/projects/:projectId',
        canActivate: [statusGuard],
        canDeactivate: [PendingChangesGuard],
        loadComponent: () => import('./screens/projects-project-summary/projects-project-summary.component').then((m) => m.ProjectsProjectSummaryComponent),
        data: { route: 'compact' },
      },
      {
        path: 'folders/:folderId/projects/:projectId/summary',
        canActivate: [statusGuard],
        loadComponent: () => import('./screens/project-summary-children/project-summary-children.component').then((m) => m.ProjectSummaryChildrenComponent),
        children: [
          {
            path: 'issues',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/issues/list/issues-list-wrapper/issues-list-wrapper.component').then((m) => m.IssuesListWrapperComponent),
            data: { route: 'issues' },
          },
          {
            path: 'signature',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/projects/signature/signature-edit-wrapper/signature-edit-wrapper.component').then((m) => m.SignatureEditWrapperComponent),
            data: { route: 'signature' },
          },
          {
            path: 'introduction',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/projects/supplementary/wrapper/supplementary-wrapper.component').then((m) => m.SupplementaryWrapperComponent),
            data: { route: 'introduction', supplementaryType: 'introduction', supplementaryTitle: 'Introduction' },
          },
          {
            path: 'summary-content',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/projects/supplementary/wrapper/supplementary-wrapper.component').then((m) => m.SupplementaryWrapperComponent),
            data: { route: 'summary-content', supplementaryType: 'summary', supplementaryTitle: 'Summary' },
          },
          {
            path: 'attachments',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/projects/attachments/project-attachments-wrapper/project-attachments-wrapper.component').then((m) => m.ProjectAttachmentsWrapperComponent),
            data: { route: 'attachments' },
          },
        ],
      },

      {
        path: 'folders/:folderId/projects/:projectId/summary/report',
        canActivate: [statusGuard],
        loadComponent: () => import('./elements/reports/report-wrapper/report-wrapper.component').then((m) => m.ReportWrapperComponent),
        data: { route: 'signature' },
      },
      {
        path: 'folders/:folderId/projects/:projectId/summary/issues/:issueId',
        canActivate: [statusGuard],
        canDeactivate: [PendingChangesGuard],
        loadComponent: () => import('./screens/issues-issue-detail/issues-issue-detail.component').then((m) => m.IssuesIssueDetailComponent),
      },
      {
        path: 'folders/:folderId/projects/:projectId/summary/issues/:issueId/:imageId',
        canActivate: [statusGuard],
        loadComponent: () => import('./elements/annotations/annotator-wrapper/annotator-wrapper.component').then((m) => m.AnnotatorWrapperComponent),
      },
      {
        path: 'settings',
        canActivate: [statusGuard],
        loadComponent: () => import('./elements/settings/wrapper/settings-wrapper/settings-wrapper.component').then((m) => m.SettingsWrapperComponent),
        children: [
          {
            path: 'company/:clientId',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/settings/clients/edit/client-edit-wrapper/client-edit-wrapper.component').then((m) => m.ClientEditWrapperComponent),
          },
          {
            path: 'company',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/settings/clients/list/clients-list-wrapper/clients-list-wrapper.component').then((m) => m.ClientsListWrapperComponent),
          },
          {
            path: 'company-profile',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/settings/company/company-wrapper/company-wrapper.component').then((m) => m.CompanyWrapperComponent),
          },
          {
            path: 'wordings',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/settings/wording/wording-wrapper/wording-wrapper.component').then((m) => m.WordingWrapperComponent),
          },
          {
            path: 'priorities',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/settings/priorities/priorities-wrapper/priorities-wrapper.component').then((m) => m.PrioritiesWrapperComponent),
          },
          {
            path: 'report-headers-footers',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/settings/report-settings/report-settings-wrapper/report-settings-wrapper.component').then((m) => m.ReportSettingsManagementWrapperComponent),
          },
          {
            path: 'assignees/:assigneeId',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/settings/assignees/edit/assignee-edit-wrapper/assignee-edit-wrapper.component').then((m) => m.AssigneeEditWrapperComponent),
          },
          {
            path: 'assignees',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/settings/assignees/list/assignees-list-wrapper/assignees-list-wrapper.component').then((m) => m.AssigneesListWrapperComponent),
          },
          {
            path: 'attachments/:attachmentId',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/settings/attachments/edit/attachment-edit-wrapper/attachment-edit-wrapper.component').then((m) => m.AttachmentEditWrapperComponent),
          },
          {
            path: 'attachments',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/settings/attachments/list/attachments-list-wrapper/attachments-list-wrapper.component').then((m) => m.AttachmentsListWrapperComponent),
          },
          {
            path: 'contact',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/settings/contact/contact-wrapper/contact-wrapper.component').then((m) => m.ContactWrapperComponent),
          },
          {
            path: 'localisation',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/settings/date-localisation/date-localisation.component').then((m) => m.DateLocalisationComponent),
          },
          {
            path: 'issue-tags',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/settings/issue-tags/list/issue-tags-list-wrapper/issue-tags-list-wrapper.component').then((m) => m.IssueTagsListWrapperComponent),
          },
          {
            path: '',
            redirectTo: 'company',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'account',
        canActivate: [statusGuard],
        loadComponent: () => import('./elements/account/wrapper/account-wrapper/account-wrapper.component').then((m) => m.AccountWrapperComponent),
        children: [
          {
            path: 'name',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/account/name/account-name/account-name.component').then((m) => m.AccountNameComponent),
          },
          {
            path: 'subscriptionandstatistics',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/account/subscription-and-statistics/subscription-and-statistics-wrapper/subscription-and-statistics-wrapper.component').then((m) => m.SubscriptionAndStatisticsWrapperComponent),
          },
          {
            path: 'email',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/account/email/reauthenticate-wrapper/reauthenticate-wrapper.component').then((m) => m.ReauthenticateWrapperComponent),
          },
          {
            path: 'password',
            canActivate: [statusGuard],
            canDeactivate: [PendingChangesGuard],
            loadComponent: () => import('./elements/account/password/change-password.component').then((m) => m.ChangePasswordComponent),
          },
          {
            path: 'userdata',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/account/user-data/request-user-data-wrapper/request-user-data-wrapper.component').then((m) => m.RequestUserDataWrapperComponent),
          },
          {
            path: 'releasenotes',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/account/release-notes/release-notes.component').then((m) => m.ReleaseNotesComponent),
          },
          {
            path: 'deleteaccount',
            canActivate: [statusGuard],
            loadComponent: () => import('./elements/account/delete-account/delete-account.component').then((m) => m.DeleteAccountComponent),
          },

          {
            path: '',
            redirectTo: 'name',
            pathMatch: 'full',
          },
        ],
      },
    ],
  },
  {
    path: 'usermanagement',
    loadComponent: () => import('./elements/authentication/user-management/user-management-wrapper/user-management-wrapper.component').then((m) => m.UserManagementWrapperComponent),
  },

  { path: '', redirectTo: 'login', pathMatch: 'full' },

  {
    path: '',
    redirectTo: 'sections/folders',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'sections/folders',
    pathMatch: 'full',
  },
];
